import { useRef, useState } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import AppInput from '../atoms/AppInput';
import InfoHint from '../atoms/InfoHint';
import { IconMoped } from '../atoms/Icon';
import { buscarCep, formatarCep } from '../api/cepApi';
import { parseDecimalInput, sanitizeDecimalInput } from '../utils/currency';

export const MODALIDADES = [
  { id: 'delivery', label: 'Entrega', hint: 'Levo até o cliente' },
  { id: 'takeaway', label: 'Retirada no balcão', hint: 'O cliente vem buscar' },
  { id: 'dine_in', label: 'Consumo no local', hint: 'O cliente come aqui' },
];

const CAMPOS_ENDERECO = ['zipCode', 'street', 'number', 'complement', 'district', 'city', 'state'];

/** Os que o backend recusa em branco. Complemento e CEP ficam de fora: nem todo
 *  endereço tem complemento, e exigir CEP de cabeça trava o cadastro por um dado
 *  que o geocodificador consegue dispensar. */
const ENDERECO_OBRIGATORIO = ['street', 'number', 'district', 'city'];

/** Estado do formulário a partir do que veio da API. `modalities` nulo no
 *  backend significa "aceita todas" — aqui isso vira as três marcadas, senão a
 *  tela abriria sem nenhuma e pareceria que o estabelecimento não atende. */
export function buildRules(establishment) {
  const endereco = establishment?.addressDetails || {};
  return {
    modalities: establishment?.modalities || MODALIDADES.map((m) => m.id),
    address: {
      ...Object.fromEntries(CAMPOS_ENDERECO.map((c) => [c, endereco[c] || ''])),
      // O backend guarda só os dígitos; quem lê a tela lê com o hífen.
      zipCode: formatarCep(endereco.zipCode),
    },
    // Só de leitura: diz se o backend conseguiu achar o endereço no mapa.
    isLocated: !!establishment?.isLocated,
    deliveryFee: precoParaCampo(establishment?.deliveryFee),
    deliveryFeePerKm: precoParaCampo(establishment?.deliveryFeePerKm),
    deliveryMaxDistanceKm: precoParaCampo(establishment?.deliveryMaxDistanceKm),
    minimumOrder: precoParaCampo(establishment?.minimumOrder),
  };
}

function precoParaCampo(valor) {
  const numero = Number(valor);
  if (!Number.isFinite(numero) || numero === 0) return '';
  // Vírgula, como se escreve preço em português; o parse aceita as duas formas.
  return String(numero).replace('.', ',');
}

const numero = (v) => parseDecimalInput(v || '0') || 0;

function enderecoPreenchido(address) {
  return CAMPOS_ENDERECO.some((c) => (address[c] || '').trim());
}

function enderecoCompleto(address) {
  return ENDERECO_OBRIGATORIO.every((c) => (address[c] || '').trim());
}

export function sameRules(a, b) {
  return (
    a.modalities.length === b.modalities.length &&
    a.modalities.every((m) => b.modalities.includes(m)) &&
    CAMPOS_ENDERECO.every((c) => (a.address[c] || '') === (b.address[c] || '')) &&
    numero(a.deliveryFee) === numero(b.deliveryFee) &&
    numero(a.deliveryFeePerKm) === numero(b.deliveryFeePerKm) &&
    numero(a.deliveryMaxDistanceKm) === numero(b.deliveryMaxDistanceKm) &&
    numero(a.minimumOrder) === numero(b.minimumOrder)
  );
}

/** O que impede de salvar, ou nulo. Fica aqui, junto das regras, para a página
 *  não precisar conhecer quais campos do endereço são obrigatórios. */
export function rulesError(rules) {
  const cobraPorDistancia = numero(rules.deliveryFeePerKm) > 0 || numero(rules.deliveryMaxDistanceKm) > 0;

  if (enderecoPreenchido(rules.address) && !enderecoCompleto(rules.address)) {
    return 'Complete o endereço do estabelecimento: rua, número, bairro e cidade.';
  }
  // Sem origem não há distância, e os dois campos ficariam configurados sem
  // efeito nenhum — o lojista só descobriria pelo valor cobrado.
  if (cobraPorDistancia && !enderecoCompleto(rules.address)) {
    return 'Informe o endereço do estabelecimento para cobrar por distância.';
  }
  return null;
}

/** Converte o estado da tela para o corpo do PATCH. */
export function rulesToPayload(rules) {
  const taxa = parseDecimalInput(rules.deliveryFee || '0');
  const porKm = parseDecimalInput(rules.deliveryFeePerKm || '0');
  const maxKm = parseDecimalInput(rules.deliveryMaxDistanceKm || '0');
  const minimo = parseDecimalInput(rules.minimumOrder || '0');

  return {
    modalities: rules.modalities,
    // Endereço incompleto não vai: o backend recusa o PATCH inteiro por ele, e
    // quem só queria mudar o horário perderia o salvamento.
    ...(enderecoCompleto(rules.address) ? { address: limparEndereco(rules.address) } : {}),
    deliveryFee: Number.isFinite(taxa) ? taxa : 0,
    deliveryFeePerKm: Number.isFinite(porKm) ? porKm : 0,
    // Zero é como o backend entende "sem limite" — é o que o campo em branco diz.
    deliveryMaxDistanceKm: Number.isFinite(maxKm) ? maxKm : 0,
    minimumOrder: Number.isFinite(minimo) ? minimo : 0,
  };
}

function limparEndereco(address) {
  return Object.fromEntries(
    CAMPOS_ENDERECO.map((c) => [c, (address[c] || '').trim() || null]),
  );
}

function DeliveryRulesForm({ value, onChange }) {
  const aceitaEntrega = value.modalities.includes('delivery');
  const temEndereco = enderecoCompleto(value.address);

  const [buscandoCep, setBuscandoCep] = useState(false);
  const [avisoCep, setAvisoCep] = useState(null);
  const numeroRef = useRef(null);

  // O valor mais recente, para uso depois do `await`. O `value` capturado no
  // corpo da função é o do render em que a consulta começou, e ela demora o
  // suficiente para o lojista digitar mais coisa nesse meio-tempo.
  const valorRef = useRef(value);
  valorRef.current = value;

  function alternar(id) {
    const proximas = value.modalities.includes(id)
      ? value.modalities.filter((m) => m !== id)
      : [...value.modalities, id];
    // Desmarcar a última deixaria o cardápio no ar sem nenhuma forma de pedir:
    // o cliente monta o carrinho e descobre no fim que não há como enviar.
    if (!proximas.length) return;
    onChange({ ...value, modalities: proximas });
  }

  const campoEndereco = (campo) => (e) =>
    onChange({ ...value, address: { ...value.address, [campo]: e.target.value } });

  function mudarEndereco(campos) {
    const atual = valorRef.current;
    onChange({ ...atual, address: { ...atual.address, ...campos } });
  }

  async function handleCep(bruto) {
    const formatado = formatarCep(bruto);
    mudarEndereco({ zipCode: formatado });
    setAvisoCep(null);

    if (formatado.replace(/\D/g, '').length !== 8) return;

    setBuscandoCep(true);
    const achado = await buscarCep(formatado);
    setBuscandoCep(false);

    if (!achado) {
      setAvisoCep('Não encontramos esse CEP. Você pode preencher à mão.');
      return;
    }

    // Só os campos que a consulta realmente trouxe. Para alguns CEPs o ViaCEP
    // devolve bairro vazio, e mesclar o vazio apagaria justamente o bairro que o
    // lojista preencheu à mão porque a consulta não trouxe.
    const trazidos = Object.fromEntries(
      Object.entries(achado).filter(([, v]) => v && v.trim()),
    );
    // Mesclado sobre o valor atual, não sobre o retrato de antes da consulta:
    // senão o número digitado enquanto ela corria seria zerado.
    mudarEndereco(trazidos);

    // O número é o único campo que a consulta não traz. Só recebe o foco se
    // estiver vazio — se o lojista já digitou, mover o cursor para lá o tira de
    // onde estava escrevendo.
    if (!valorRef.current.address.number?.trim()) numeroRef.current?.focus();
  }

  const campoDecimal = (campo) => (e) =>
    onChange({ ...value, [campo]: sanitizeDecimalInput(e.target.value) });

  return (
    <Card className="border-0 card-elevated" style={{ borderRadius: 'var(--radius-md)' }}>
      <Card.Body className="p-4">
        <div className="d-flex align-items-center gap-2 mb-1">
          <IconMoped size={16} style={{ color: 'var(--color-purple-primary)' }} />
          <h6 className="fw-semibold mb-0" style={{ color: 'var(--color-neutral)' }}>
            Formas de atendimento
          </h6>
        </div>
        <p className="mb-3" style={{ fontSize: 12, color: '#888' }}>
          O cardápio do cliente só oferece o que estiver marcado aqui.
        </p>

        {MODALIDADES.map((m) => (
          <div key={m.id} className="d-flex align-items-center gap-2 py-2">
            <Form.Check
              type="switch"
              id={`modalidade-${m.id}`}
              checked={value.modalities.includes(m.id)}
              onChange={() => alternar(m.id)}
              aria-label={m.label}
            />
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{m.label}</div>
              <div style={{ fontSize: 11.5, color: '#999' }}>{m.hint}</div>
            </div>
          </div>
        ))}

        {/* O endereço da loja fica dentro deste cartão, e não no de dados
            cadastrais, porque é daqui que ele serve: é a origem da distância.
            Longe do campo "por km" ele pareceria burocracia. */}
        {aceitaEntrega && (
          <>
            <div className="mt-4 mb-2" style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>
              Endereço do estabelecimento
            </div>

            <div className="form-row-split">
              <div style={{ flex: '0 1 160px' }}>
                <AppInput
                  label="CEP"
                  value={value.address.zipCode}
                  onChange={(e) => handleCep(e.target.value)}
                  placeholder="00000-000"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={9}
                  hint={buscandoCep ? 'Buscando endereço…' : undefined}
                  error={avisoCep || undefined}
                />
              </div>
              <div className="flex-grow-1">
                <AppInput
                  label="Rua"
                  value={value.address.street}
                  onChange={campoEndereco('street')}
                  placeholder="Ex: Avenida Brasil"
                  autoComplete="address-line1"
                />
              </div>
            </div>

            <div className="form-row-split">
              <div style={{ flex: '0 1 120px' }}>
                <AppInput
                  label="Número"
                  ref={numeroRef}
                  value={value.address.number}
                  onChange={campoEndereco('number')}
                  placeholder="123"
                  inputMode="numeric"
                />
              </div>
              <div className="flex-grow-1">
                <AppInput
                  label="Complemento"
                  value={value.address.complement}
                  onChange={campoEndereco('complement')}
                  placeholder="Opcional"
                />
              </div>
            </div>

            <div className="form-row-split">
              <div className="flex-grow-1">
                <AppInput
                  label="Bairro"
                  value={value.address.district}
                  onChange={campoEndereco('district')}
                  placeholder="Ex: Centro"
                />
              </div>
              <div className="flex-grow-1">
                <AppInput
                  label="Cidade"
                  value={value.address.city}
                  onChange={campoEndereco('city')}
                  placeholder="Ex: Porto Alegre"
                  autoComplete="address-level2"
                />
              </div>
              <div style={{ flex: '0 1 90px' }}>
                <AppInput
                  label="UF"
                  value={value.address.state}
                  onChange={(e) => mudarEndereco({ state: e.target.value.toUpperCase().slice(0, 2) })}
                  placeholder="RS"
                  maxLength={2}
                  autoComplete="address-level1"
                />
              </div>
            </div>

            {/* Endereço salvo que o mapa não achou: a taxa por km passa a cobrar
                só a parcela fixa, e sem este aviso o lojista descobriria isso
                pelo caixa no fim do dia. */}
            {temEndereco && !value.isLocated && (
              <InfoHint>
                Ainda não localizamos este endereço no mapa. Confira rua, número e cidade —
                enquanto isso, a entrega cobra apenas a taxa base.
              </InfoHint>
            )}
          </>
        )}

        <div className="form-row-split mt-3">
          <div className="flex-grow-1">
            <AppInput
              label="Taxa base (R$)"
              type="text"
              inputMode="decimal"
              value={value.deliveryFee}
              onChange={campoDecimal('deliveryFee')}
              placeholder="0,00"
              disabled={!aceitaEntrega}
              autoComplete="off"
            />
          </div>
          <div className="flex-grow-1">
            <AppInput
              label="Por km (R$)"
              type="text"
              inputMode="decimal"
              value={value.deliveryFeePerKm}
              onChange={campoDecimal('deliveryFeePerKm')}
              placeholder="0,00"
              disabled={!aceitaEntrega}
              autoComplete="off"
            />
          </div>
          <div className="flex-grow-1">
            <AppInput
              label="Entrego até (km)"
              type="text"
              inputMode="decimal"
              value={value.deliveryMaxDistanceKm}
              onChange={campoDecimal('deliveryMaxDistanceKm')}
              placeholder="Sem limite"
              disabled={!aceitaEntrega}
              autoComplete="off"
            />
          </div>
        </div>

        {!aceitaEntrega && (
          <InfoHint style={{ marginTop: -8 }}>
            Essas regras só valem para entrega, que está desligada.
          </InfoHint>
        )}
        {aceitaEntrega && (
          <InfoHint>
            A taxa é <strong>base + por km × distância</strong>, medida em linha reta entre a
            loja e o cliente. Deixe "por km" em branco para cobrar uma taxa única.
          </InfoHint>
        )}

        <div className="form-row-split mt-3">
          <div className="flex-grow-1">
            <AppInput
              label="Pedido mínimo (R$)"
              type="text"
              inputMode="decimal"
              value={value.minimumOrder}
              onChange={campoDecimal('minimumOrder')}
              placeholder="0,00"
              autoComplete="off"
            />
          </div>
        </div>
        <InfoHint>
          O mínimo é conferido sobre os itens, sem a taxa de entrega. Deixe em branco para não exigir mínimo.
        </InfoHint>
      </Card.Body>
    </Card>
  );
}

export default DeliveryRulesForm;
